import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export type QueryParameterConfig<T, K = T> = {
  decode: (value?: Array<null | string> | null | string) => K | T;
  default: K;
  encode: (value?: T) => string | string[] | undefined;
};

export type SearchValues = Record<string, string | string[] | undefined>;

type SearchKey = string | string[];

export function useQueryParameter<T, K = undefined>(paramKey: string, config: QueryParameterConfig<T, K>) {
  const [searchParams] = useSearchParams();

  const getSearchValues = useCallback(
    ({ key }: { key: SearchKey }) => {
      const keys = Array.isArray(key) ? key : [key];

      for (const searchKey of keys) {
        if (searchParams.has(searchKey)) {
          const value = searchParams.getAll(searchKey);

          if (value.length > 1) {
            return value;
          }

          return searchParams.get(searchKey);
        }
      }

      return undefined;
    },
    [searchParams],
  );

  const decodedValue = useMemo(
    () =>
      config.decode(
        getSearchValues({
          key: paramKey,
        }),
      ),
    [config, getSearchValues, paramKey],
  );

  return decodedValue;
}
